import { BACKOFFICE_API_BASE, APP_DOMAIN } from 'utils/urls';

/**
 * Shared pieces of the native login completion, used by both entry points:
 * the deep-link callback after a password login (useNativeLoginDeepLink) and
 * the biometric unlock (redirectToLogin -> startBiometricLogin).
 */

export type ExchangedSession = { sessionJwt: string; refreshJwt: string };

/**
 * 'rejected'  — a response came back with no access_token: Descope looked at
 *               the token and said no. The credential is genuinely dead.
 * 'network'   — every attempt failed to even get a response. The credential
 *               itself was never actually checked, so it may well still be
 *               good — this is a connectivity problem, not a credential one.
 * The two must not be handled the same way: only 'rejected' warrants
 * disabling the stored biometric sign-in (AC9). 'network' should just offer
 * the player another way in and leave the enrollment alone.
 */
export type ExchangeFailureReason = 'rejected' | 'network';

export type ExchangeResult =
  | ({ ok: true } & ExchangedSession)
  | { ok: false; reason: ExchangeFailureReason };

function engineHttpUrl(): string {
  return (
    process.env.REACT_APP_WS_URL?.replace('wss://', 'https://').replace('ws://', 'http://') ||
    `https://engine.${APP_DOMAIN}`
  );
}

// Bounded retry for a TRANSIENT failure only (dropped connection, hung
// request) — see exchangeRefreshToken for why this is safe here but would not
// be for a one-time OAuth code. Several attempts on purpose: the player should
// see the app genuinely try to recover a flaky connection before it gives up
// and offers a different sign-in method, not bail after one blip.
const EXCHANGE_MAX_ATTEMPTS = 3;
const EXCHANGE_RETRY_DELAY_MS = 750;
const EXCHANGE_TIMEOUT_MS = 8_000;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Linear backoff (750ms, 1500ms, 2250ms, …) — enough spacing to give a flaky
// connection room to recover without making the player wait through a long
// silent pause between attempts, especially now those attempts are visible.
function retryDelayFor(attempt: number): number {
  return EXCHANGE_RETRY_DELAY_MS * attempt;
}

function generateRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for a WebView without crypto.randomUUID — still unique enough
  // for a 60s idempotency window, just not cryptographically random.
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

async function fetchToken(refreshToken: string, requestId: string): Promise<unknown> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), EXCHANGE_TIMEOUT_MS);
  try {
    // POST with the token in the body, not a query string — a refresh token is
    // a bearer credential and must not end up in proxy/ALB/access logs.
    //
    // requestId is the SAME value across every retry of this one logical
    // exchange (generated once by the caller) — the engine caches its answer
    // under that key for a short window, so a retry replays the cached result
    // instead of re-asking Descope to rotate an already-rotated token.
    const res = await fetch(`${engineHttpUrl()}/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken, requestId }),
      signal: controller.signal
    });
    return await res.json();
  } finally {
    clearTimeout(timeout);
  }
}

/**
 * Trade a Descope refresh token for a full session via the engine.
 *
 * Note this ROTATES the refresh token — the endpoint performs an OAuth2
 * `grant_type=refresh_token` against Descope (apps/engine/httpServer.js), which
 * invalidates the one passed in. Anything holding a copy (the biometric
 * keystore entry) has to be re-armed with the returned token afterwards.
 *
 * A dropped connection or a timeout on OUR end doesn't actually tell us
 * whether Descope's rotate call landed before the response was lost — a slow
 * network or proxy timeout AFTER Descope answered is indistinguishable, from
 * here, from one that never reached Descope at all. Retrying blindly in that
 * ambiguous case risks resending an already-rotated (and now dead) refresh
 * token, which can trip Descope's rotation-reuse detection. `requestId` (one
 * value, generated once below, reused across every attempt of this call)
 * makes the retry safe regardless: the engine caches its answer under that
 * key for a short window, so a retry replays the cached result instead of
 * re-asking Descope to rotate the same token twice (EN-5415).
 *
 * An explicit rejection (a response came back with no access_token) means
 * Descope already looked at the token and said no — retrying that would just
 * ask the same question again, so it fails immediately instead.
 *
 * `onRetry` fires right before each retry (with the upcoming attempt number)
 * so a caller can surface it — QA asked for a retry that is visible, not one
 * that only shows up in the console — see BiometricUnlockPrompt's "Reconnecting…"
 * toast, driven off biometricLoginEntry's subscribeExchangeRetry.
 */
export async function exchangeRefreshToken(
  refreshToken: string,
  onRetry?: (attempt: number, maxAttempts: number) => void
): Promise<ExchangeResult> {
  const requestId = generateRequestId();
  for (let attempt = 1; attempt <= EXCHANGE_MAX_ATTEMPTS; attempt++) {
    let token: any;
    try {
      token = await fetchToken(refreshToken, requestId);
    } catch (e) {
      const isLastAttempt = attempt === EXCHANGE_MAX_ATTEMPTS;
      console.warn(
        `[NativeLogin] token exchange attempt ${attempt}/${EXCHANGE_MAX_ATTEMPTS} failed:`,
        (e as Error)?.message || e
      );
      if (isLastAttempt) return { ok: false, reason: 'network' };
      onRetry?.(attempt + 1, EXCHANGE_MAX_ATTEMPTS);
      await delay(retryDelayFor(attempt));
      continue;
    }
    if (token?.access_token) {
      return {
        ok: true,
        sessionJwt: token.access_token,
        refreshJwt: token.refresh_token || refreshToken,
      };
    }
    // The engine explicitly flagged this as ITS OWN transient/infra issue
    // (e.g. it couldn't reach Descope in time, or hit a Redis blip) — a
    // definitive Descope answer never arrived, so this is safe to retry the
    // same way a network exception is (EN-5415). Only a response with no
    // `retryable` signal at all (a rejection, or an older engine build that
    // predates this field) settles immediately below.
    if (token?.retryable) {
      const isLastAttempt = attempt === EXCHANGE_MAX_ATTEMPTS;
      console.warn(
        `[NativeLogin] token exchange attempt ${attempt}/${EXCHANGE_MAX_ATTEMPTS} reported retryable:`,
        token?.error || ''
      );
      if (isLastAttempt) return { ok: false, reason: 'network' };
      onRetry?.(attempt + 1, EXCHANGE_MAX_ATTEMPTS);
      await delay(retryDelayFor(attempt));
      continue;
    }
    console.warn('[NativeLogin] token exchange returned no access_token', token?.error || '');
    return { ok: false, reason: 'rejected' };
  }
  return { ok: false, reason: 'network' };
}

async function fetchNativeHandoffExchange(code: string): Promise<unknown> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), EXCHANGE_TIMEOUT_MS);
  try {
    const res = await fetch(`${engineHttpUrl()}/native-handoff/exchange`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
      signal: controller.signal
    });
    return await res.json();
  } finally {
    clearTimeout(timeout);
  }
}

/**
 * Redeem a one-time native-handoff code (EN-5415) for a full session — this
 * is what the hijackpoker:// deep link now carries instead of a raw refresh
 * token. A custom URL scheme is not a verified recipient (another app can
 * register/claim it, notably on Android), so whatever an unverified handler
 * could intercept there needs to be worth as little as possible: a code
 * that's single-use and dead within ~90 seconds, rather than a long-lived,
 * directly reusable refresh token.
 *
 * The code is single-use server-side, but retrying THIS call is still safe:
 * the engine caches the redemption result under the code itself (same
 * requestId-style idempotency as exchangeRefreshToken above), so a lost
 * response replays the cached session instead of hitting "already used".
 */
export async function exchangeNativeHandoffCode(
  code: string,
  onRetry?: (attempt: number, maxAttempts: number) => void
): Promise<ExchangeResult> {
  for (let attempt = 1; attempt <= EXCHANGE_MAX_ATTEMPTS; attempt++) {
    let token: any;
    try {
      token = await fetchNativeHandoffExchange(code);
    } catch (e) {
      const isLastAttempt = attempt === EXCHANGE_MAX_ATTEMPTS;
      console.warn(
        `[NativeLogin] native-handoff code exchange attempt ${attempt}/${EXCHANGE_MAX_ATTEMPTS} failed:`,
        (e as Error)?.message || e
      );
      if (isLastAttempt) return { ok: false, reason: 'network' };
      onRetry?.(attempt + 1, EXCHANGE_MAX_ATTEMPTS);
      await delay(retryDelayFor(attempt));
      continue;
    }
    if (token?.access_token) {
      return {
        ok: true,
        sessionJwt: token.access_token,
        refreshJwt: token.refresh_token || ''
      };
    }
    // See exchangeRefreshToken's identical branch above — the engine flags
    // its own transient/infra failures (e.g. "code already used" caused by
    // its own in-flight claim, or a Redis blip) as retryable, distinct from a
    // genuine "Descope said no" (EN-5415).
    if (token?.retryable) {
      const isLastAttempt = attempt === EXCHANGE_MAX_ATTEMPTS;
      console.warn(
        `[NativeLogin] native-handoff code exchange attempt ${attempt}/${EXCHANGE_MAX_ATTEMPTS} reported retryable:`,
        token?.error || ''
      );
      if (isLastAttempt) return { ok: false, reason: 'network' };
      onRetry?.(attempt + 1, EXCHANGE_MAX_ATTEMPTS);
      await delay(retryDelayFor(attempt));
      continue;
    }
    console.warn(
      '[NativeLogin] native-handoff code exchange returned no access_token',
      token?.error || ''
    );
    return { ok: false, reason: 'rejected' };
  }
  return { ok: false, reason: 'network' };
}

// Values deriveStatus (apps/auth) treats as "let them in". Anything else routes
// the player somewhere first — Jumio, support, phone entry, rewards opt-in.
const ID_VERIFIED_OK = ['approved', 'suspected'];

/**
 * Whether a biometric login may go straight into the app, or has to hand off to
 * the auth page for routing.
 *
 * Biometric enrollment can only happen when the account is already OK (the
 * prompt lives inside the ACCOUNT_STATUS.OK branch of LoginApp), so the case
 * this guards is a status that changed AFTER enrollment: an account later
 * denied, flagged duplicate, or due for re-verification. Skipping the auth page
 * for those would quietly bypass the KYC routing they're supposed to hit.
 *
 * This calls the same server-verified endpoints the password login path uses
 * (apps/auth's checkAccountRestriction + getUser, both backed by a live
 * Descope/DB lookup) rather than decoding attributes out of the client-held
 * session JWT — a decode can't be trusted to reflect current account state and
 * has no signal to distinguish "not restricted" from "lookup failed".
 *
 * Fails CLOSED on any lookup failure or network error, matching
 * checkAccountRestriction's own posture: this is the security backstop that
 * keeps a since-restricted or since-flagged account out of the fast path, so an
 * unreachable backend must route to the auth page, not wave the player through.
 */
export async function isAccountStatusOk(loginId: string, sessionJwt: string): Promise<boolean> {
  const authHeader = { Authorization: `Bearer ${sessionJwt}` };
  try {
    const restrictionRes = await fetch(
      `${BACKOFFICE_API_BASE}/descope/checkAccountRestriction?loginId=${encodeURIComponent(loginId)}`,
      { headers: authHeader }
    );
    if (!restrictionRes.ok) return false;
    const restriction = await restrictionRes.json();
    if (restriction?.restricted) return false;

    const userRes = await fetch(`${BACKOFFICE_API_BASE}/descope/getUser`, {
      method: 'POST',
      headers: { ...authHeader, 'Content-Type': 'application/json' },
      body: JSON.stringify({ loginId })
    });
    if (!userRes.ok) return false;
    const body = await userRes.json();
    const attrs = body?.data?.customAttributes || {};

    if (attrs.duplicateDocumentNum && String(attrs.duplicateDocumentNum).trim() !== '') {
      return false;
    }
    const idVerified = attrs.idVerified;
    if (idVerified === undefined || idVerified === null) return false;
    return ID_VERIFIED_OK.includes(String(idVerified).trim().toLowerCase());
  } catch {
    return false;
  }
}
